import React, {memo} from 'react';
import {createPortal} from 'react-dom';
import {twMerge} from 'tailwind-merge';
import {
	AlertCircle,
	AlertTriangle,
	CheckCircle2,
	Info,
	LoaderCircle,
	X,
} from 'lucide-react';

const typeStyles = {
	info: {
		icon: Info,
		iconClassName: 'text-blue-600',
	},
	success: {
		icon: CheckCircle2,
		iconClassName: 'text-green-600',
	},
	warning: {
		icon: AlertTriangle,
		iconClassName: 'text-amber-600',
	},
	error: {
		icon: AlertCircle,
		iconClassName: 'text-red-600',
	},
	loading: {
		icon: LoaderCircle,
		iconClassName: 'text-gray-500 animate-spin',
	},
};

const Toast = memo(({type = 'info', message, onClose, className}) => {
	const style = typeStyles[type] || typeStyles.info;
	const Icon = style.icon;

	return (
		<div
			className={twMerge(
				'pointer-events-auto flex items-start gap-3 rounded-lg border border-gray-200 bg-white px-4 py-3 shadow-lg',
				className,
			)}
			role="status"
			aria-live={type === 'error' ? 'assertive' : 'polite'}
		>
			<Icon className={twMerge('mt-0.5 size-4 shrink-0', style.iconClassName)} />
			<div className="min-w-0 flex-1 break-words text-sm text-gray-800">{message}</div>
			{typeof onClose === 'function' ? (
				<button
					type="button"
					onClick={onClose}
					className="rounded p-0.5 text-gray-400 hover:text-gray-600"
					aria-label="Close notification"
				>
					<X className="size-4" />
				</button>
			) : null}
		</div>
	);
});

Toast.displayName = 'Toast';

export const ToastContainer = memo(({children, className, portalTarget = 'popper'}) => {
	const content = (
		<div
			className={twMerge(
				'pointer-events-none fixed top-4 right-4 z-[1600] flex w-[calc(100vw-2rem)] max-w-[420px] flex-col gap-2',
				className,
			)}
		>
			{children}
		</div>
	);

	if (typeof document === 'undefined') return content;
	const portalElement = document.getElementById(portalTarget);
	if (!portalElement) return content;
	return createPortal(content, portalElement);
});

ToastContainer.displayName = 'ToastContainer';

export default Toast;
